/** Typed CRC Floor Sheet blocks stored on `floorsheet.welds[i]`. */

export type {
  FloorSheetFsTimeSlot,
  FloorSheetFsTimes
} from './floorSheetFsTimes'
export {
  emptyFsTimes,
  normalizeFsTimes,
  formatFsTimeSlotDisplay
} from './floorSheetFsTimes'

export type FloorSheetFsHeader = {
  procedureTitle: string
  pupNumber: string
}

export type FloorSheetIwmHeadCell = {
  amps: string
  volts: string
  gas: string
  wfs: string
}

export type FloorSheetIwmHead = {
  head: number
  cw: FloorSheetIwmHeadCell
  ccw: FloorSheetIwmHeadCell
}

export type FloorSheetMisfirePosition = 'top' | 'side' | 'bottom'
export type FloorSheetMisfireSide = 'CW' | 'CCW'

export type FloorSheetMisfireCell = {
  amps1: string
  amps2: string
  volts1: string
  volts2: string
  arcTime: string
  length: string
  travel: string
}

export const MISFIRE_POSITIONS: FloorSheetMisfirePosition[] = ['top', 'side', 'bottom']
export const MISFIRE_SIDES: FloorSheetMisfireSide[] = ['CW', 'CCW']

export type MisfireMetaFieldKey = keyof Pick<
  FloorSheetMisfireRecord,
  'completedBy' | 'polarity' | 'wireFeedSpeed' | 'powerSupply' | 'process' | 'progression'
>

export const MISFIRE_META_FIELDS: Array<{
  key: MisfireMetaFieldKey
  label: string
  unit?: string
}> = [
  { key: 'completedBy', label: 'Misfire Completed By' },
  { key: 'polarity', label: 'Polarity' },
  { key: 'wireFeedSpeed', label: 'Wire Feed Speed', unit: 'in/min' },
  { key: 'powerSupply', label: 'Power Supply' },
  { key: 'process', label: 'Process' },
  { key: 'progression', label: 'Progression' }
]

export type MisfireMatrixField = keyof FloorSheetMisfireCell

export const MISFIRE_POSITION_LABELS: Record<FloorSheetMisfirePosition, string> = {
  top: 'Top',
  side: 'Side',
  bottom: 'Bottom'
}

export type MisfireParamRow =
  | {
      kind: 'dual'
      id: 'amps' | 'volts'
      label: string
      field1: 'amps1' | 'volts1'
      field2: 'amps2' | 'volts2'
    }
  | {
      kind: 'single'
      id: 'arcTime' | 'length' | 'travel'
      label: string
      unit: string
      field: 'arcTime' | 'length' | 'travel'
    }

/** Parameter rows under each CW / CCW side (Amps/Volts = dual inputs per position). */
export const MISFIRE_PARAM_ROWS: MisfireParamRow[] = [
  { kind: 'dual', id: 'amps', label: 'Amps', field1: 'amps1', field2: 'amps2' },
  { kind: 'dual', id: 'volts', label: 'Volts', field1: 'volts1', field2: 'volts2' },
  { kind: 'single', id: 'arcTime', label: 'Arc Time', unit: 'sec', field: 'arcTime' },
  { kind: 'single', id: 'length', label: 'Length', unit: 'in', field: 'length' },
  { kind: 'single', id: 'travel', label: 'Travel', unit: 'in/min', field: 'travel' }
]

export function misfireParamRowLabel(row: MisfireParamRow): string {
  return row.kind === 'single' ? `${row.label} (${row.unit})` : row.label
}

export function misfireMetaFieldLabel(field: (typeof MISFIRE_META_FIELDS)[number]): string {
  return field.unit ? `${field.label} (${field.unit})` : field.label
}

/** Show one or two amps/volts readings depending on what was entered. */
export function formatMisfireDualValue(v1: string, v2: string): string {
  const parts = [v1, v2].map((v) => v.trim()).filter(Boolean)
  return parts.length ? parts.join(' - ') : '—'
}

/** Length / Travel display: always one decimal place when numeric (e.g. 15 → 15.0). */
export function formatMisfireOneDecimal(value: string): string {
  const trimmed = (value ?? '').trim()
  if (!trimmed || trimmed === '—') return '—'
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return trimmed
  return n.toFixed(1)
}

export type FloorSheetMisfireRecord = {
  powerSupply: string
  polarity: string
  process: string
  progression: string
  wireFeedSpeed: string
  completedBy: string
  /** Team member UUID for the Completed By dropdown (persisted selection). */
  completedByMemberId: string
  cells: {
    CW: Record<FloorSheetMisfirePosition, FloorSheetMisfireCell>
    CCW: Record<FloorSheetMisfirePosition, FloorSheetMisfireCell>
  }
}

export type FloorSheetParamRecord = {
  iwmHeads: FloorSheetIwmHead[]
  misfire: FloorSheetMisfireRecord
}

export type FloorSheetDeposition = {
  unit: 'in' | 'mm'
  calculatedTopOfGroove: string
  afterRoot: Record<string, string>
  afterCaps: Record<string, string>
  byPass: Record<string, Record<string, string>>
}

/** @deprecated Process targets removed from CRC Target Values UI; kept for hydrate. */
export type FloorSheetTargets = {
  travelSpeed: string
  wfs: string
  amps: string
  volts: string
  gas: string
}

/** Persisted CRC Target Values scalars (settings → weld → PDF). */
export type FloorSheetFsTargetValues = {
  maxJointMisalignment: string
  fillAngleA: string
  offsetE: string
  shelfHeight: string
  landThickness: string
}

const emptyCell = (): FloorSheetIwmHeadCell => ({ amps: '', volts: '', gas: '', wfs: '' })
const emptyMisfireCell = (): FloorSheetMisfireCell => ({
  amps1: '',
  amps2: '',
  volts1: '',
  volts2: '',
  arcTime: '',
  length: '',
  travel: ''
})

function normalizeMisfireCell(raw: unknown): FloorSheetMisfireCell {
  const c = asRecord(raw)
  const legacyAmps = String(c?.amps ?? '')
  const legacyVolts = String(c?.volts ?? '')
  return {
    amps1: String(c?.amps1 ?? legacyAmps),
    amps2: String(c?.amps2 ?? ''),
    volts1: String(c?.volts1 ?? legacyVolts),
    volts2: String(c?.volts2 ?? ''),
    arcTime: String(c?.arcTime ?? ''),
    length: String(c?.length ?? ''),
    travel: String(c?.travel ?? '')
  }
}

export function emptyIwmHeads(): FloorSheetIwmHead[] {
  return [1, 2, 3, 4].map((head) => ({
    head,
    cw: emptyCell(),
    ccw: emptyCell()
  }))
}

export function emptyMisfireRecord(): FloorSheetMisfireRecord {
  const side = (): Record<FloorSheetMisfirePosition, FloorSheetMisfireCell> => ({
    top: emptyMisfireCell(),
    side: emptyMisfireCell(),
    bottom: emptyMisfireCell()
  })
  return {
    powerSupply: '',
    polarity: '',
    process: '',
    progression: '',
    wireFeedSpeed: '',
    completedBy: '',
    completedByMemberId: '',
    cells: { CW: side(), CCW: side() }
  }
}

export function emptyParamRecord(): FloorSheetParamRecord {
  return { iwmHeads: emptyIwmHeads(), misfire: emptyMisfireRecord() }
}

export function emptyDeposition(locations: string[]): FloorSheetDeposition {
  const clock = () =>
    locations.reduce((acc, loc) => {
      acc[loc] = ''
      return acc
    }, {} as Record<string, string>)
  return {
    unit: 'in',
    calculatedTopOfGroove: '',
    afterRoot: clock(),
    afterCaps: clock(),
    byPass: {}
  }
}

export function emptyFsHeader(): FloorSheetFsHeader {
  return { procedureTitle: '', pupNumber: '' }
}

export function emptyFsTargets(): FloorSheetTargets {
  return { travelSpeed: '', wfs: '', amps: '', volts: '', gas: '' }
}

export function emptyFsTargetValues(): FloorSheetFsTargetValues {
  return {
    maxJointMisalignment: '',
    fillAngleA: '',
    offsetE: '',
    shelfHeight: '',
    landThickness: ''
  }
}

export function normalizeFsTargetValues(raw: unknown): FloorSheetFsTargetValues {
  const o = asRecord(raw)
  return {
    maxJointMisalignment: String(o?.maxJointMisalignment ?? ''),
    fillAngleA: String(o?.fillAngleA ?? ''),
    offsetE: String(o?.offsetE ?? ''),
    shelfHeight: String(o?.shelfHeight ?? ''),
    landThickness: String(o?.landThickness ?? '')
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

export function normalizeFsHeader(raw: unknown): FloorSheetFsHeader {
  const o = asRecord(raw)
  return {
    procedureTitle: String(o?.procedureTitle ?? ''),
    pupNumber: String(o?.pupNumber ?? '')
  }
}

export function normalizeFsTargets(raw: unknown): FloorSheetTargets {
  const o = asRecord(raw)
  return {
    travelSpeed: String(o?.travelSpeed ?? ''),
    wfs: String(o?.wfs ?? ''),
    amps: String(o?.amps ?? ''),
    volts: String(o?.volts ?? ''),
    gas: String(o?.gas ?? '')
  }
}

function remapClock(src: unknown, locations: string[]): Record<string, string> {
  const o = asRecord(src) ?? {}
  return locations.reduce((acc, loc) => {
    acc[loc] = String(o[loc] ?? '')
    return acc
  }, {} as Record<string, string>)
}

export function normalizeDeposition(raw: unknown, locations: string[]): FloorSheetDeposition {
  const o = asRecord(raw)
  const byPassRaw = asRecord(o?.byPass) ?? {}
  const byPass: Record<string, Record<string, string>> = {}
  for (const [pass, clocks] of Object.entries(byPassRaw)) {
    byPass[pass] = remapClock(clocks, locations)
  }
  return {
    unit: o?.unit === 'mm' ? 'mm' : 'in',
    calculatedTopOfGroove: String(o?.calculatedTopOfGroove ?? ''),
    afterRoot: remapClock(o?.afterRoot, locations),
    afterCaps: remapClock(o?.afterCaps, locations),
    byPass
  }
}

function normalizeHeadCell(raw: unknown): FloorSheetIwmHeadCell {
  const o = asRecord(raw)
  return {
    amps: String(o?.amps ?? ''),
    volts: String(o?.volts ?? ''),
    gas: String(o?.gas ?? ''),
    wfs: String(o?.wfs ?? '')
  }
}

export function normalizeParamRecord(raw: unknown): FloorSheetParamRecord {
  const o = asRecord(raw)
  const headsRaw = Array.isArray(o?.iwmHeads) ? o!.iwmHeads : []
  const defaults = emptyIwmHeads()
  const iwmHeads = defaults.map((d, i) => {
    const src = asRecord(headsRaw[i])
    return {
      head: d.head,
      cw: normalizeHeadCell(src?.cw),
      ccw: normalizeHeadCell(src?.ccw)
    }
  })
  const misfireSrc = asRecord(o?.misfire)
  const empty = emptyMisfireRecord()
  const readPos = (
    side: Record<string, unknown> | null,
    pos: FloorSheetMisfirePosition
  ): FloorSheetMisfireCell => normalizeMisfireCell(side?.[pos])
  const cw = asRecord(asRecord(misfireSrc?.cells)?.CW)
  const ccw = asRecord(asRecord(misfireSrc?.cells)?.CCW)
  return {
    iwmHeads,
    misfire: {
      powerSupply: String(misfireSrc?.powerSupply ?? empty.powerSupply),
      polarity: String(misfireSrc?.polarity ?? ''),
      process: String(misfireSrc?.process ?? ''),
      progression: String(misfireSrc?.progression ?? ''),
      wireFeedSpeed: String(misfireSrc?.wireFeedSpeed ?? ''),
      completedBy: String(misfireSrc?.completedBy ?? ''),
      completedByMemberId: String(misfireSrc?.completedByMemberId ?? ''),
      cells: {
        CW: {
          top: readPos(cw, 'top'),
          side: readPos(cw, 'side'),
          bottom: readPos(cw, 'bottom')
        },
        CCW: {
          top: readPos(ccw, 'top'),
          side: readPos(ccw, 'side'),
          bottom: readPos(ccw, 'bottom')
        }
      }
    }
  }
}

export function remapDepositionLocations(
  deposition: FloorSheetDeposition,
  locations: string[]
): FloorSheetDeposition {
  const pick = (src: Record<string, string>) =>
    locations.reduce((acc, loc) => {
      acc[loc] = src[loc] ?? ''
      return acc
    }, {} as Record<string, string>)
  const byPass: Record<string, Record<string, string>> = {}
  for (const [pass, clocks] of Object.entries(deposition.byPass)) {
    byPass[pass] = pick(clocks)
  }
  return {
    ...deposition,
    afterRoot: pick(deposition.afterRoot),
    afterCaps: pick(deposition.afterCaps),
    byPass
  }
}
